import React, { useEffect, useState, useCallback, useMemo } from "react";
import Swal from "sweetalert2";
import {
  AddPlan,
  AdminProfile,
  GetAllStratgy,
  getChartingStrategyTag,
  AddOnPlan
} from "../../CommonAPI/Admin";
import AddForm from "../../../ExtraComponent/FormData";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import Content from "../../../ExtraComponent/Content";
import { ChartingPlatformSegment } from "../../CommonAPI/User";

// Custom Multi Select with Select All
const CustomMultiSelect = ({ label, options, selected, onChange, disabled }) => {
  const customStyles = {
    option: (provided) => ({
      ...provided,
      color: "black",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  const hasSelectAll = options.some(opt => opt.value === "__select_all__");
  const allOptionsWithoutSelectAll = hasSelectAll 
    ? options.filter(opt => opt.value !== "__select_all__")
    : options;

  const handleChange = (selectedOptions) => {
    if (!selectedOptions) {
      onChange([]);
      return;
    }

    const hasSelectedAll = selectedOptions.find(opt => opt.value === "__select_all__");
    
    if (hasSelectedAll) {
      onChange(allOptionsWithoutSelectAll);
    } else {
      onChange(selectedOptions);
    }
  };

  const optionsWithSelectAll = selected?.length === allOptionsWithoutSelectAll.length
    ? options.filter(opt => opt.value !== "__select_all__")
    : [{ value: "__select_all__", label: "Select All" }, ...allOptionsWithoutSelectAll];

  return (
    <div className="col-lg-6">
      <h6>{label}</h6>
      <Select
        isMulti
        options={optionsWithSelectAll}
        value={selected}
        onChange={handleChange}
        isDisabled={disabled}
        styles={customStyles}
      />
    </div>
  );
};

const AddPlanPage = () => {
  const navigate = useNavigate();
  const adminPermission = localStorage.getItem("adminPermission");
  const selectedPlanTab = sessionStorage.getItem("SelectedPlanTab");

  // State Management
  const [selecteScalping, setSelecteScalping] = useState([]);
  const [selecteOptions, setSelecteOptions] = useState([]);
  const [selectePattern, setSelectePattern] = useState([]);
  const [selectedCharting, setSelectedCharting] = useState([]);
  const [selectedStrategyTags, setSelectedStrategyTags] = useState([]);
  const [selecteGoldenStrategy, setSelecteGoldenStrategy] = useState([]);
  const [scalpingStratgy, setScalpingStratgy] = useState([]);
  const [OptionStratgy, setOptionStratgy] = useState([]);
  const [PatternStratgy, setPatternStratgy] = useState([]);
  const [strategyTags, setStrategyTags] = useState([]);
  const [adminProfile, setAdminProfile] = useState(null);
  const [chartingSegmentOption, setChartingSegmentOption] = useState([]);

  const planOptionArr = useMemo(() => {
    const arr = [{ value: "Scalping", label: "SOP" }];
    if (adminPermission?.includes("ChartingPlatform")) {
      arr.push({ value: "Charting", label: "Charting" });
    }
    if (!adminPermission?.includes("OverallPlan")) {
      arr.push({ value: "AddOn", label: "AddOn" });
    }
    return arr;
  }, [adminPermission]);

  // Utility Functions
  const showError = useCallback((title, message = "") => {
    Swal.fire({
      backdrop: "#121010ba",
      confirmButtonColor: "#1ccc8a",
      title,
      text: message || "An error occurred.",
      icon: "error",
      timer: 1500,
      timerProgressBar: true,
    });
  }, []);

  const showSuccess = useCallback((message) => {
    Swal.fire({
      backdrop: "#121010ba",
      confirmButtonColor: "#1ccc8a",
      title: "Success!",
      text: message,
      icon: "success",
      timer: 1500,
      timerProgressBar: true,
    });
  }, []);

  // API Calls
  const fetchStrategyTags = useCallback(async () => {
    try {
      const res = await getChartingStrategyTag();
      if (res?.data) {
        const tags = res.data.map((tag) => ({
          value: tag.Strategytag,
          label: tag.Strategytag,
        }));
        setStrategyTags(tags);
      }
    } catch (err) {
      showError("Failed to fetch strategy tags");
    }
  }, [showError]);

  const GetScalpingStratgy = useCallback(async () => {
    try {
      const response = await GetAllStratgy();
      if (response.Status) {
        setScalpingStratgy(Object.values(response.Scalping));
        setPatternStratgy(Object.values(response.Pattern));
        setOptionStratgy(Object.values(response.Option));
      } else {
        showError("Failed to fetch strategies", response.message);
      }
    } catch (err) {
      showError("Network error occurred");
    }
  }, [showError]);

  const fetchAdminDetials = useCallback(async () => {
    try {
      const response = await AdminProfile();
      setAdminProfile(response.Data[0]);
    } catch (err) {
      showError("Network error occurred");
    }
  }, [showError]);

  const fetchChartingSegments = useCallback(async () => {
    try {
      const response = await ChartingPlatformSegment();
      setChartingSegmentOption(response?.Segment || []);
    } catch (error) {
      console.error("Error fetching charting segments:", error);
    }
  }, []);

  // Effects
  useEffect(() => {
    GetScalpingStratgy();
    fetchStrategyTags();
    fetchAdminDetials();
    fetchChartingSegments();
  }, [GetScalpingStratgy, fetchStrategyTags, fetchAdminDetials, fetchChartingSegments]);

  // Validation Schema
  const validationSchema = Yup.object({
    planname: Yup.string()
      .matches(/^[A-Za-z_\s]+$/, "Only letters, underscores and spaces allowed")
      .required("Plan name is required"),
    NumberofScript: Yup.number().when("PlanType", {
      is: (val) => val === "Scalping" || val === "AddOn",
      then: (schema) =>
        schema
          .typeError("Number of scripts must be a number")
          .min(1, "Number of scripts must be greater than 0")
          .required("Number of scripts is required"),
    }),
    SOPPrice: Yup.number().when("PlanType", {
      is: "Scalping",
      then: (schema) =>
        !adminPermission?.includes("OverallPlan")
          ? schema
              .typeError("Payment must be a number")
              .min(1, "Payment must be greater than 0")
              .required("Payment is required")
          : schema.notRequired(),
    }),
    Price: Yup.number().when("PlanType", {
      is: "AddOn",
      then: (schema) =>
        schema
          .typeError("Price must be a number")
          .min(1, "Price must be greater than 0")
          .required("Price is required"),
    }),
    Duration: Yup.string().when("PlanType", {
      is: (val) => val !== "AddOn",
      then: (schema) => schema.required("Please select a plan duration"),
    }),
    Charting: Yup.array().when("PlanType", {
      is: "Charting",
      then: (schema) =>
        schema
          .min(1, "Please select at least one Segment")
          .required("Please select at least one Segment"),
    }),
  });

  // Formik
  const formik = useFormik({
    initialValues: {
      NumberofScript: "",
      SOPPrice: 0,
      planname: "",
      Duration: "One_Month",
      PlanType: selectedPlanTab || "Scalping",
      SOPLiveTrade: 0,
      SOPPaperTrade: 0,
      Charting: [],
      ChartPerMonth: "",
      ChartPaperTrade: 0.0,
      Price: 0.0,
      Overallprice: 0.0,
    },
    validationSchema,
    onSubmit: async (values) => {
      if (
        values.PlanType === "Scalping" &&
        selecteScalping.length === 0 &&
        selecteOptions.length === 0 &&
        selectePattern.length === 0 &&
        selecteGoldenStrategy.length === 0
      ) {
        showError(
          "Error!",
          "Please select at least one strategy either Scalping, Option, Pattern or Golden Strategy."
        );
        return;
      }

      if( values.PlanType === "Charting" ){
        if(   selectedCharting.length === 0 ){
          showError("Error!", "Please select at least one Segment.");
          return; 
        }
        if(values.SOPPrice === "" || isNaN(values.SOPPrice) ){
          showError("Error!", "Charting price must be a valid number.");
          return;
        }
        if(values.SOPPrice <= 0 ){
          showError("Error!", "Charting price must be greater than 0.");
          return;
        }
        if(selectedStrategyTags.length == 0 ){
          showError("Error!", "Please select at least one Strategy Tag.");
          return;
        }
      }

      if( adminPermission?.includes("OverallPlan")){
          if(values.Overallprice === "" || isNaN(values.Overallprice) ){
            showError("Error!", "Overall price must be a valid number.");
            return;
          }
        if( values.Overallprice <= 0  ){
          showError("Error!", "Overall price must be greater than 0.");
          return;
        }
        if( selectedCharting.length === 0 ){
          showError("Error!", "Please select at least one Segment.");
          return;
        }
        if(selectedStrategyTags.length === 0 ){
          showError("Error!", "Please select at least one Strategy Tag.");
          return;
        }
        if(selecteScalping.length == 0 ){
          showError("Error!", "Please select at least one Scalping strategy.");
          return;
        }
       if(selecteOptions.length == 0 ){
         showError("Error!", "Please select at least one Option strategy.");
         return;
       }
       if(selectePattern.length == 0 ){
         showError("Error!", "Please select at least one Pattern strategy.");
         return;
       }

      }

      const req = {
        Scalping: selecteScalping.map((strategy) => strategy.value),
        Option: selecteOptions.map((strategy) => strategy.value),
        PatternS: selectePattern.map((strategy) => strategy.value),
        NumberofScript:  adminPermission?.includes("OverallPlan") ? 5 : values.PlanType === "Scalping" ? values.NumberofScript : 0,
        planname: values.planname,
        Duration: values.Duration,
        Charting: selectedCharting.map((chart) => chart.value),
        Strategytag: selectedStrategyTags.map((tag) => tag.value),
        ...(values.PlanType === "Scalping"
          ? {
              SOPPrice: values.SOPPrice,
              SOPLiveTrade: values.SOPLiveTrade || 0,
            }
          : {
              ChartPerMonth: values.SOPPrice,
              SOPPrice: 0,
              ChartLiveTrade: values.SOPLiveTrade || 0,
            }),
        SOPPaperTrade: values.SOPPaperTrade || 0.0,
        ChartPaperTrade: values.ChartPaperTrade || 0.0,
        GoldenStrategy: selecteGoldenStrategy.map((strategy) => strategy.value),
        Overallprice: values.Overallprice || 0.0,
      };

      try {
         const reqforAddOn = {
                Price: values.Price || 0.0,
                NumberofScript: values.NumberofScript || 0,
                Planname: values.planname,
            } 

              if (formik.values.PlanType === "AddOn") {
                    const response = await AddOnPlan(reqforAddOn);
                 
                    if (response.Status) {
                        Swal.fire({
                            // background: "#1a1e23 ",
                            backdrop: "#121010ba",
                            confirmButtonColor: "#1ccc8a",
                            title: "Success!",
                            text: response.message,
                            icon: "success",
                            timer: 1500,
                            timerProgressBar: true
                        });
                        setTimeout(() => {
                            sessionStorage.setItem("redirectPlan", formik.values.PlanType);
                            navigate('/admin/allplan');
                        }, 1500);
                    }
                    else {
                        Swal.fire({
                            // background: "#1a1e23 ",
                            backdrop: "#121010ba",
                            confirmButtonColor: "#1ccc8a",
                            title: "Error!",
                            text: response.message,
                            icon: "error",
                            timer: 1500,
                            timerProgressBar: true
                        });
                    }

                }else{

                  const response = await AddPlan(req);
                  if (response.Status) {
                    showSuccess(response.message);
                    setTimeout(() => {
                      sessionStorage.setItem("redirectPlan", values.PlanType);
                      navigate("/admin/allplan");
                    }, 1500);
                }
              }
      
      } catch (err) {
        showError("An unexpected error occurred");
      }
    },
  });

  // Handlers
  const handleChartingChange = useCallback((selected) => {
    setSelectedCharting(selected);
    formik.setFieldValue("Charting", selected);
  }, [formik]);

  const handleStrategyTagChange = useCallback((selected) => {
    setSelectedStrategyTags(selected);
  }, []);

  const handleSelectChange = useCallback((type, selected) => {
    const setters = {
      scalping: setSelecteScalping,
      option: setSelecteOptions,
      pattern: setSelectePattern,
      goldenstrategy: setSelecteGoldenStrategy,
    };
    
    setters[type]?.(selected);
    
    if (!adminPermission?.includes("OverallPlan")) {
      setSelectedCharting([]);
    }
  }, [adminPermission]);

  // Memoized Options
  const scalpingOptions = useMemo(
    () => scalpingStratgy.map((strategy) => ({ value: strategy, label: strategy })),
    [scalpingStratgy]
  );

  const optionOptions = useMemo(
    () => OptionStratgy.map((strategy) => ({ value: strategy, label: strategy })),
    [OptionStratgy]
  );

  const patternOptions = useMemo(
    () => PatternStratgy.map((strategy) => ({ value: strategy, label: strategy })),
    [PatternStratgy]
  );

  const chartingOptions = useMemo(
    () => chartingSegmentOption.map((segment) => ({ value: segment, label: segment })),
    [chartingSegmentOption]
  );

  const goldenStrategyOptions = useMemo(
    () => ["MomentumPicker", "Jobbing"].map((strategy) => ({ value: strategy, label: strategy })),
    []
  );

  // Form Fields Configuration
  const fields = useMemo(() => [
    {
      name: "PlanType",
      label: "Plan Type",
      type: "select",
      options: planOptionArr,
      col_size: 6,
      showWhen: () => !adminPermission?.includes("OverallPlan"),
    },
    {
      name: "planname",
      label: "Plan Name",
      type: "text",
      col_size: 6,
    },
    {
      name: "NumberofScript",
      label: "Number of Script",
      type: "text3",
      showWhen: (values) => values.PlanType === "Scalping" || values.PlanType === "AddOn",
      col_size: 6,
    },
    {
      name: "Price",
      label: "Price",
      type: "text3",
      col_size: 6,
      showWhen: (values) => values.PlanType === "AddOn",
    },
    {
      name: "SOPPrice",
      label: "Payment",
      type: "text3",
      col_size: 6,
      showWhen: (values) =>
        adminPermission?.includes("OverallPlan") ? false : values.PlanType !== "AddOn",
    },
    {
      name: "Duration",
      label: "Duration",
      type: "select",
      options: [
        { value: "One_Month", label: "One Month" },
        { value: "Quarterly", label: "Quarterly" },
        { value: "Half_Yearly", label: "Half Yearly" },
        { value: "Yearly", label: "Yearly" },
      ],
      col_size: 6,
      showWhen: (values) => values.PlanType !== "AddOn",
    },
    {
      name: "SOPLiveTrade",
      label: "Live Trade Amount",
      type: "text",
      col_size: 6,
      showWhen: (values) =>
        adminPermission?.includes("OverallPlan")
          ? false
          : (values.PlanType === "Scalping" && adminProfile?.SOPLiveTrade) ||
            (values.PlanType !== "Scalping" && adminProfile?.ChartLiveTrade),
    },
    {
      name: "SOPPaperTrade",
      label: "Paper Trade Amount",
      type: "text",
      showWhen: () =>
        adminPermission?.includes("OverallPlan")
          ? false
          : formik.values.PlanType === "Scalping" && adminProfile?.SOPPaperTrade,
      col_size: 6,
    },
    {
      name: "ChartPaperTrade",
      label: "Paper Trade Amount",
      type: "text",
      showWhen: () =>
        adminPermission?.includes("OverallPlan")
          ? false
          : formik.values.PlanType !== "Scalping" && adminProfile?.ChartPaperTrade,
      col_size: 6,
    },
    {
      name: "Overallprice",
      label: "Overall price",
      type: "text",
      col_size: 6,
      showWhen: () => adminPermission?.includes("OverallPlan"),
    },
  ], [planOptionArr, adminPermission, adminProfile, formik.values.PlanType]);

  const showOverallOrCharting = adminPermission?.includes("OverallPlan") || formik.values.PlanType === "Charting";
  const showOverallOrScalping = adminPermission?.includes("OverallPlan") || formik.values.PlanType === "Scalping";

  return (
    <Content
      Page_title={" ➕ Add Plan"}
      button_status={false}
      backbutton_status={false}
    >
      <nav aria-label="breadcrumb"> 
        <ol className="breadcrumb d-sm-flex align-items-center justify-content-between d-block">
          <li className="breadcrumb-item active " aria-current="page">
            Add Plan
          </li>
        </ol>
      </nav>
      <div className="card-bg-color">
<AddForm
        className="admin-add-btn"
        fields={fields.filter((field) => !field.showWhen || field.showWhen(formik.values))}
        btn_name="Add"
        btn_name1="Cancel"
        formik={formik}
        btn_name1_route="/admin/allplan"
        additional_field={
          <>
            {showOverallOrCharting && (
              <>
                <div className="col-lg-6 dropdown-multi">
                  <CustomMultiSelect
                    label={<span className="card-text-Color">Segment</span>}
                    options={chartingOptions}
                    selected={selectedCharting}
                    onChange={handleChartingChange}
                  />
                  {formik.touched.Charting && formik.errors.Charting && (
                    <div className="text-danger mt-1 small">{formik.errors.Charting}</div>
                  )}
                </div>
                <div className="col-lg-6 dropdown-multi" >
                  <CustomMultiSelect
                    label={<span className="card-text-Color ">Strategy Tag</span>}
                    options={strategyTags}
                    selected={selectedStrategyTags}
                    onChange={handleStrategyTagChange}
                  />
                </div>
              </>
            )}
            
            {showOverallOrScalping && (
              <>
                <CustomMultiSelect
                  label={<span className="card-text-Color">Scalping</span>}
                  options={scalpingOptions}
                  selected={selecteScalping}
                  onChange={(selected) => handleSelectChange("scalping", selected)}
                />
                <CustomMultiSelect
                  label={<span className="card-text-Color">Option</span>}
                  options={optionOptions}
                  selected={selecteOptions}
                  onChange={(selected) => handleSelectChange("option", selected)}
                />
                <CustomMultiSelect
                  label={<span className="card-text-Color">Pattern</span>}
                  options={patternOptions}
                  selected={selectePattern}
                  onChange={(selected) => handleSelectChange("pattern", selected)}
                />
              </>
            )}
            
            {formik.values.PlanType === "Scalping" && adminPermission?.includes("Golden Strategy") && (
              <CustomMultiSelect
                label={<span className="card-text-Color mt-2">Golden Strategy</span>}
                options={goldenStrategyOptions}
                selected={selecteGoldenStrategy}
                onChange={(selected) => handleSelectChange("goldenstrategy", selected)}
              />
            )}
          </>
        }
      />
      </div>
      
    </Content>
  );
};

export default AddPlanPage;